import { useEffect, useState } from "react";
import { UserPlus, Trash2, Copy, Mail, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PageHeader, Field } from "../components/Shared";
import { api, fmtDate, formatApiError } from "../lib/api";
import { ROLES, ROLE_DESCRIPTIONS } from "../lib/labels";
import { useAuth } from "../context/AuthContext";

const INVITABLE = ["commercial", "chef_equipe", "equipier"];

function RoleSelect({ value, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-44"><SelectValue /></SelectTrigger>
      <SelectContent>{INVITABLE.map((r) => <SelectItem key={r} value={r}>{ROLES[r]}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function InviteDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ email: "", name: "", role: "commercial" });
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (open) { setForm({ email: "", name: "", role: "commercial" }); setResult(null); }
  }, [open]);
  const submit = async () => {
    try {
      const { data } = await api.post("/team/invite", form);
      setResult(data);
      onSaved();
      toast.success(data.email_sent ? "Invitation envoyée par e-mail" : "Invitation créée (e-mail non envoyé, partagez le lien)");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="invite-dialog">
        <DialogHeader><DialogTitle>Inviter un membre</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-3 text-sm" data-testid="invite-result">
            <p>{result.email_sent ? `Un e-mail a été envoyé à ${result.email}.` : "L'e-mail n'a pas pu être envoyé."} Vous pouvez aussi partager ce lien (valable 7 jours) :</p>
            <div className="flex gap-2">
              <Input readOnly value={result.invite_url} data-testid="invite-link-input" />
              <Button variant="outline" size="icon" data-testid="invite-copy-button" onClick={() => { navigator.clipboard?.writeText(result.invite_url); toast.success("Lien copié"); }}><Copy className="h-4 w-4" /></Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Field label="Nom"><Input data-testid="invite-member-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="E-mail"><Input data-testid="invite-member-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Rôle"><RoleSelect value={form.role} onChange={(role) => setForm({ ...form, role })} testid="invite-role-select" /></Field>
            <p className="text-xs text-slate-500">{ROLE_DESCRIPTIONS[form.role]}</p>
          </div>
        )}
        <DialogFooter>
          {result ? <Button onClick={onClose} data-testid="invite-close-button">Fermer</Button>
            : <Button className="bg-[#0b2c6b]" onClick={submit} data-testid="invite-submit-button"><Mail className="mr-2 h-4 w-4" />Envoyer l'invitation</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Team() {
  const { user } = useAuth();
  const [data, setData] = useState({ users: [], invitations: [] });
  const [open, setOpen] = useState(false);
  const load = () => api.get("/team").then((r) => setData(r.data));
  useEffect(() => {
    load();
  }, []);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fade-up">
      <PageHeader overline="Entreprise" title="Équipe & accès" subtitle="Invitez vos commerciaux et chefs d'équipe, chacun avec son propre accès.">
        <Button data-testid="invite-member-button" className="bg-[#0b2c6b] hover:bg-[#1559d6]" onClick={() => setOpen(true)}><UserPlus className="mr-2 h-4 w-4" />Inviter</Button>
      </PageHeader>
      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        {Object.entries(ROLES).map(([k, v]) => (
          <div key={k} className="rounded-xl border border-slate-200 bg-white p-4"><p className="text-sm font-semibold text-slate-900">{v}</p><p className="mt-1 text-xs text-slate-500">{ROLE_DESCRIPTIONS[k]}</p></div>
        ))}
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <Table data-testid="team-table">
          <TableHeader><TableRow><TableHead>Membre</TableHead><TableHead>Rôle</TableHead><TableHead>Depuis</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.users.map((m) => {
              const locked = m.role === "owner" || m.id === user.id;
              return (
                <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                  <TableCell><p className="font-semibold text-slate-900">{m.name}</p><p className="text-xs text-slate-500">{m.email}</p></TableCell>
                  <TableCell>{locked ? <span className="text-sm font-medium">{ROLES[m.role]}</span> : <RoleSelect value={m.role} testid={`member-role-${m.id}`} onChange={(role) => act(() => api.put(`/team/users/${m.id}`, { role }), "Rôle mis à jour")} />}</TableCell>
                  <TableCell className="text-sm text-slate-600">{fmtDate(m.created_at)}</TableCell>
                  <TableCell className="text-right">
                    {!locked && <Button size="icon" variant="ghost" data-testid={`remove-member-${m.id}`} onClick={() => window.confirm(`Retirer l'accès de ${m.name} ?`) && act(() => api.delete(`/team/users/${m.id}`), "Accès retiré")}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                  </TableCell>
                </TableRow>
              );
            })}
            {data.invitations.map((i) => (
              <TableRow key={i.id} className="bg-slate-50/60" data-testid={`invite-row-${i.id}`}>
                <TableCell><p className="font-semibold text-slate-700">{i.name}</p><p className="text-xs text-slate-500">{i.email}</p></TableCell>
                <TableCell className="text-sm">{ROLES[i.role]}</TableCell>
                <TableCell className="text-xs text-amber-700"><Clock className="mr-1 inline h-3 w-3" />Invitation en attente · expire le {fmtDate(i.expires_at)}</TableCell>
                <TableCell className="text-right"><Button size="icon" variant="ghost" data-testid={`cancel-invite-${i.id}`} onClick={() => act(() => api.delete(`/team/invitations/${i.id}`), "Invitation annulée")}><Trash2 className="h-4 w-4 text-red-500" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <InviteDialog open={open} onClose={() => setOpen(false)} onSaved={load} />
    </div>
  );
}
